import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import SuccessSpine from "../components/site/SuccessSpine";
import { trackLeadSubmitted } from "../lib/analytics";

const INITIAL = { name: "", phone: "", concern: "", preferred_time: "", consent: true };

/** Merge UTM params from the current URL for lead-source tracking */
function readUtm() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "website",
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
  };
}

/**
 * useLeadForm — owns form state, validation and submit for any lead-capture form.
 * Params:
 *   category      — condition category tag ("acl", "back-pain", "general", …)
 *   sourcePage    — analytic page identifier
 *   defaultConcern — pre-fills the concern textarea (optional)
 */
export function useLeadForm({ category = "general", sourcePage = "unknown", defaultConcern = "" } = {}) {
  const [form, setForm] = useState({ ...INITIAL, concern: defaultConcern });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number.");
      return false;
    }
    if (!form.consent) {
      toast.error("Please accept the consent checkbox.");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        condition_category: category,
        source_page: sourcePage,
        ...readUtm(),
      });
      trackLeadSubmitted({
        condition_category: category,
        source_page: sourcePage,
        utm_source: readUtm().utm_source,
      });
      toast.custom(() => (
        <div className="flex items-center gap-3 bg-white border border-brand-cyan/40 shadow-lg rounded-2xl p-3 pr-5" data-testid="lead-toast">
          <SuccessSpine size={48} />
          <div>
            <div className="font-heading text-brand-ink">Thank you — request received</div>
            <div className="text-xs text-brand-ink2">We'll be in touch shortly.</div>
          </div>
        </div>
      ), { duration: 4500 });
      setForm({ ...INITIAL, concern: defaultConcern });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return { form, set, loading, submit };
}
